import { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axios';

// Data types based on API DTOs
export interface StatisticsKPI {
  total_pnl: number;
  win_rate: number;
  loss_rate: number;
  average_win: number;
  average_loss: number;
  total_trades: number;
  winning_trades: number;
  losing_trades: number;
  best_trade: number;
  worst_trade: number;
  max_drawdown: number;
  profit_factor: number | null;
  total_investment: number;
  roi_percent: number;
  trader_id: number;
  period_start?: string;
  period_end?: string;
}

export interface PerformanceHistoryItem {
  date: string;
  daily_pnl: number;
  cumulative_pnl: number;
  total_transactions: number;
}

export interface PerformanceHistory {
  data: PerformanceHistoryItem[];
  trader_id: number;
  period_start?: string;
  period_end?: string;
  total_records: number;
}

export interface UseStatisticsParams {
  traderId: number;
  startDate?: string;
  endDate?: string;
}

export interface UseStatisticsReturn {
  kpis: StatisticsKPI | null;
  performanceHistory: PerformanceHistory | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// Cache interface
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// Cache TTL: 5 minutes
const CACHE_TTL = 5 * 60 * 1000;

// Simple in-memory cache
const cache = new Map<string, CacheEntry<any>>();

const getCacheKey = (traderId: number, startDate?: string, endDate?: string) => {
  return `${traderId}-${startDate || 'null'}-${endDate || 'null'}`;
};

const getCachedData = <T>(key: string): T | null => {
  const entry = cache.get(key);
  if (!entry) return null;
  
  if (Date.now() - entry.timestamp > CACHE_TTL) {
    cache.delete(key);
    return null;
  }
  
  return entry.data;
};

const setCachedData = <T>(key: string, data: T): void => {
  cache.set(key, { data, timestamp: Date.now() });
};

// Retry utility with exponential backoff
const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: Error;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      
      if (attempt === maxRetries - 1) break;
      
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw new Error(lastError!.message);
};

export const useStatistics = ({
  traderId,
  startDate,
  endDate
}: UseStatisticsParams): UseStatisticsReturn => {
  const [kpis, setKpis] = useState<StatisticsKPI | null>(null);
  const [performanceHistory, setPerformanceHistory] = useState<PerformanceHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!traderId) return;

    setLoading(true);
    setError(null);

    try {
      const cacheKey = getCacheKey(traderId, startDate, endDate);
      
      // Check cache first
      const cachedKpis = getCachedData<StatisticsKPI>(`kpis-${cacheKey}`);
      const cachedHistory = getCachedData<PerformanceHistory>(`history-${cacheKey}`);
      
      if (cachedKpis && cachedHistory) {
        setKpis(cachedKpis);
        setPerformanceHistory(cachedHistory);
        setLoading(false);
        return;
      }

      // Build query parameters
      const params: Record<string, any> = { trader_id: traderId };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      // Fetch both endpoints with retry logic
      const [kpisResponse, historyResponse] = await Promise.all([
        retryWithBackoff(() => 
          axios.get('/statistics/kpis', { params })
        ),
        retryWithBackoff(() => 
          axios.get('/statistics/performance-history', { params })
        )
      ]);

      const kpisData = kpisResponse.data;
      const historyData = historyResponse.data;


      // Cache the results
      setCachedData(`kpis-${cacheKey}`, kpisData);
      setCachedData(`history-${cacheKey}`, historyData);

      setKpis(kpisData);
      setPerformanceHistory(historyData);
    } catch (err: any) {
      let errorMessage = 'An unexpected error occurred';
      
      if (err.response) {
        switch (err.response.status) {
          case 404:
            errorMessage = 'Trader not found';
            break;
          case 500:
            errorMessage = 'Service temporarily unavailable';
            break;
          default:
            errorMessage = `Server error: ${err.response.status}`;
        }
      } else if (err.request) {
        errorMessage = 'Network error - please check your connection';
      }
      
      setError(errorMessage);
      setKpis(null);
      setPerformanceHistory(null);
    } finally {
      setLoading(false);
    }
  }, [traderId, startDate, endDate]);

  const refetch = useCallback(() => {
    // Clear cache for this trader/date range
    const cacheKey = getCacheKey(traderId, startDate, endDate);
    cache.delete(`kpis-${cacheKey}`);
    cache.delete(`history-${cacheKey}`);
    
    fetchData();
  }, [fetchData, traderId, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    kpis,
    performanceHistory,
    loading,
    error,
    refetch
  };
};
